import { Helmet } from 'react-helmet-async';
import { Phone, Clock, Shield, Zap, CheckCircle2, AlertCircle, HeartPulse } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

export default function PflegeNotfall() {
  return (
    <>
      <Helmet>
        <title>Pflege-Notfall Itzehoe | Sofort-Hilfe 24/7 | Phönix Pflegeteam</title>
        <meta name="description" content="Pflege-Notfall in Itzehoe? ✓ Wir helfen JETZT ✓ 24/7 Hotline: 0151 46173685 ✓ Reaktionszeit unter 4 Stunden ✓ Auch Wochenende & Feiertage ✓ Start ab morgen möglich" />
        <link rel="canonical" href="https://pflegeteam-itzehoe.de/pflege-notfall" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EmergencyService",
            "name": "24/7 Pflege-Notdienst Kreis Steinburg",
            "description": "Notfall-Pflegedienst für Itzehoe und Umgebung mit Reaktionszeit unter 4 Stunden",
            "provider": {
              "@id": "https://pflegeteam-itzehoe.de/#organization"
            },
            "telephone": "+4915146173685",
            "availableChannel": {
              "@type": "ServiceChannel",
              "servicePhone": {
                "@type": "ContactPoint",
                "telephone": "+4915146173685",
                "contactType": "emergency",
                "hoursAvailable": {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
                  "opens": "00:00",
                  "closes": "23:59"
                }
              }
            },
            "areaServed": {
              "@type": "State",
              "name": "Schleswig-Holstein"
            }
          })}
        </script>
      </Helmet>

      <main id="main-content">
        {/* Hero - EXTREME Dringlichkeit */}
        <section className="relative pt-24 pb-16 overflow-hidden bg-gradient-to-br from-red-50 via-white to-orange-50">
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-100 rounded-full blur-3xl opacity-30" />
          
          <div className="container-custom relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              {/* Notfall-Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 rounded-full mb-6 animate-pulse">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="text-sm font-bold text-red-600">NOTFALL-HOTLINE</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1e293b] leading-tight mb-6">
                Pflege-Notfall in <span className="text-red-600">Itzehoe</span>?<br />
                Wir helfen <span className="text-[#1A52D8]">sofort!</span>
              </h1>

              {/* RIESIGER CTA */}
              <div className="bg-gradient-to-r from-red-600 to-red-500 rounded-3xl p-8 md:p-12 shadow-2xl mb-8">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <Phone className="w-8 h-8 text-white animate-bounce" />
                  <span className="text-white text-lg font-semibold">JETZT ANRUFEN:</span>
                </div>
                <a href="tel:+4915146173685" className="block">
                  <div className="text-5xl md:text-6xl font-bold text-white mb-3 tracking-wide hover:scale-105 transition-transform">
                    0151 46173685
                  </div>
                </a>
                <div className="flex items-center justify-center gap-2 text-white/90">
                  <Clock className="w-5 h-5" />
                  <span className="font-semibold">24 Stunden • 7 Tage • 365 Tage</span>
                </div>
              </div>

              {/* Sofort-Versprechen */}
              <div className="grid md:grid-cols-3 gap-4 mb-8">
                <div className="bg-white rounded-xl p-4 border-2 border-green-200">
                  <CheckCircle2 className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <div className="font-bold text-[#1e293b] mb-1">Rückruf in 10 Min</div>
                  <div className="text-sm text-[#64748b]">Wir melden uns sofort</div>
                </div>
                <div className="bg-white rounded-xl p-4 border-2 border-green-200">
                  <Zap className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <div className="font-bold text-[#1e293b] mb-1">Vor Ort in 4 Std</div>
                  <div className="text-sm text-[#64748b]">Bei echten Notfällen</div>
                </div>
                <div className="bg-white rounded-xl p-4 border-2 border-green-200">
                  <HeartPulse className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <div className="font-bold text-[#1e293b] mb-1">Start ab morgen</div>
                  <div className="text-sm text-[#64748b]">Reguläre Pflege</div>
                </div>
              </div>

              <p className="text-lg text-[#64748b]">
                <strong className="text-[#1e293b]">Auch Wochenende, Feiertage, nachts:</strong> Wir lassen Sie nicht allein.
              </p>
            </div>
          </div>
        </section>

        {/* Typische Notfall-Situationen */}
        <section className="py-16 bg-white">
          <div className="container-custom max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-8 text-center">
              Typische Pflege-Notfälle - Wir kennen das
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Nach Krankenhausentlassung',
                  description: 'Patient wird entlassen, aber zuhause ist niemand vorbereitet. Wir übernehmen sofort.',
                  example: '„Mein Vater kommt morgen aus dem Klinikum - ich schaffe die Pflege nicht allein!"'
                },
                {
                  title: 'Pflegende Angehörige ausgefallen',
                  description: 'Tochter/Sohn erkrankt oder muss dringend weg. Pflege bricht zusammen.',
                  example: '„Ich bin selbst krank geworden - wer pflegt jetzt meine Mutter?"'
                },
                {
                  title: 'Bisheriger Dienst hat gekündigt',
                  description: 'Pflegedienst fällt aus oder kündigt. Keine Zeit für lange Suche.',
                  example: '„Unser Pflegedienst hat abgesagt - was jetzt?"'
                },
                {
                  title: 'Plötzliche Verschlechterung',
                  description: 'Zustand hat sich akut verschlechtert. Mehr Pflege nötig, schnell!',
                  example: '„Nach dem Sturz kann Opa nicht mehr allein - sofortige Hilfe nötig!"'
                }
              ].map((item, i) => (
                <div key={i} className="bg-gradient-to-br from-[#f8fafb] to-white rounded-xl p-6 border border-[#e2e8f0]">
                  <h3 className="font-bold text-[#1e293b] mb-3 text-lg">{item.title}</h3>
                  <p className="text-[#64748b] mb-3">{item.description}</p>
                  <blockquote className="text-sm italic text-[#94a3b8] border-l-2 border-[#23D3D3] pl-3">
                    {item.example}
                  </blockquote>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Wie es funktioniert - 3 Schritte */}
        <section className="py-16 bg-[#f8fafb]">
          <div className="container-custom max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-12 text-center">
              So funktioniert Notfall-Pflege - 3 einfache Schritte
            </h2>

            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Sofort anrufen',
                  description: 'Rufen Sie uns unter 0151 46173685 an - egal ob 3 Uhr nachts oder Sonntag. Ein echter Mensch nimmt ab (kein Anrufbeantworter!).',
                  time: 'Jetzt'
                },
                {
                  step: '2',
                  title: 'Situation besprechen',
                  description: 'In 10-15 Minuten klären wir: Was ist passiert? Was wird benötigt? Wer ist vor Ort? Haben Sie schon einen Pflegegrad? Wir finden sofort eine Lösung.',
                  time: '+10 Minuten'
                },
                {
                  step: '3',
                  title: 'Pflege startet',
                  description: 'Je nach Dringlichkeit: Notfallbesuch noch heute (bei akuter Gefahr) oder regulärer Start morgen. Wir organisieren alles - Sie müssen sich um nichts kümmern.',
                  time: '+4 Stunden oder +24h'
                }
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1A52D8] to-[#23D3D3] flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                      {item.step}
                    </div>
                  </div>
                  <div className="flex-1 bg-white rounded-xl p-6 border border-[#e2e8f0]">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-[#1e293b] text-xl">{item.title}</h3>
                      <span className="text-sm text-[#23D3D3] font-semibold">{item.time}</span>
                    </div>
                    <p className="text-[#64748b] leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-12 text-center">
              <a href="tel:+4915146173685">
                <Button className="bg-red-600 hover:bg-red-700 text-white h-16 px-12 rounded-full text-xl font-bold shadow-2xl">
                  <Phone className="w-6 h-6" />
                  JETZT ANRUFEN: 0151 46173685
                </Button>
              </a>
              <p className="text-sm text-[#64748b] mt-4">
                Auch nachts • Auch Wochenende • Auch Feiertage
              </p>
            </div>
          </div>
        </section>

        {/* Was wir NICHT sind */}
        <section className="py-16 bg-white">
          <div className="container-custom max-w-3xl mx-auto">
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-[#1e293b] mb-2">Wichtiger Hinweis:</h3>
                  <p className="text-[#475569] mb-3">
                    Wir sind ein <strong>Pflegedienst</strong>, kein medizinischer Rettungsdienst.
                  </p>
                  <p className="text-[#64748b] text-sm">
                    Bei <strong>lebensbedrohlichen Notfällen</strong> (Herzinfarkt, Schlaganfall, schwere Verletzungen, Atemnot) 
                    rufen Sie bitte sofort die <strong className="text-red-600">112</strong> an!
                  </p>
                  <p className="text-[#64748b] text-sm mt-2">
                    Wir helfen bei: Plötzlichem Pflegebedarf, Versorgungslücken, Angehörigen-Ausfall, 
                    Pflege-Organisation nach Krankenhaus, akutem Betreuungsbedarf.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Servicebereich Notfall */}
        <section className="py-16 bg-[#f8fafb]">
          <div className="container-custom max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-8 text-center">
              Notfall-Pflege in allen Orten des Kreis Steinburg
            </h2>

            <div className="bg-white rounded-2xl p-8 border border-[#e2e8f0]">
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-[#64748b]">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#23D3D3] flex-shrink-0" />
                  <span>Itzehoe (Reaktionszeit: 15 Min)</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#23D3D3] flex-shrink-0" />
                  <span>Dägeling (Reaktionszeit: 5 Min)</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#23D3D3] flex-shrink-0" />
                  <span>Glückstadt (Reaktionszeit: 25 Min)</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#23D3D3] flex-shrink-0" />
                  <span>Kellinghusen (Reaktionszeit: 25 Min)</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#23D3D3] flex-shrink-0" />
                  <span>Wilster (Reaktionszeit: 30 Min)</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#23D3D3] flex-shrink-0" />
                  <span>Krempe (Reaktionszeit: 25 Min)</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#23D3D3] flex-shrink-0" />
                  <span>Hohenlockstedt (Reaktionszeit: 25 Min)</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#23D3D3] flex-shrink-0" />
                  <span>Alle weiteren Orte: unter 35 Min</span>
                </div>
              </div>

              <p className="text-center text-sm text-[#94a3b8] mt-6">
                Reaktionszeiten sind Richtwerte für Notfall-Einsätze. Reguläre Pflegetermine planen wir individuell nach Ihren Wünschen.
              </p>
            </div>
          </div>
        </section>

        {/* Persönliche Message */}
        <section className="py-16 bg-white">
          <div className="container-custom max-w-2xl mx-auto text-center">
            <div className="mb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#1A52D8] to-[#23D3D3] mx-auto mb-4 flex items-center justify-center text-white text-3xl font-bold">
                LF
              </div>
              <h3 className="text-2xl font-bold text-[#1e293b] mb-2">Lisa Fayazi</h3>
              <p className="text-[#64748b]">Geschäftsführerin & Examinierte Altenpflegerin</p>
            </div>
            
            <blockquote className="text-lg text-[#475569] leading-relaxed italic">
              „Als Mutter von zwei Kindern weiß ich, wie es sich anfühlt, wenn man dringend Hilfe braucht. 
              Pflege-Notfälle sind Ausnahmesituationen - niemand plant das. Deshalb haben wir unsere 24/7-Hotline: 
              Damit Sie nicht allein sind. Egal wann, egal wo im Kreis Steinburg - wir kommen."
            </blockquote>
          </div>
        </section>

        {/* FAQ Notfall */}
        <section className="py-16 bg-[#f8fafb]">
          <div className="container-custom max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-8 text-center">
              Häufige Fragen zum Notfall-Service
            </h2>

            <div className="space-y-4">
              {[
                {
                  q: "Kostet der Notfall-Service extra?",
                  a: "Nein! Es gibt keine Nacht-, Wochenend- oder Feiertagszuschläge bei uns. Auch Notfall-Einsätze kosten regulär 45-55€/Stunde und werden von der Pflegekasse übernommen. Wenn Sie noch keinen Pflegegrad haben, helfen wir kostenlos beim Antrag - rückwirkend erstattet die Kasse dann die Kosten."
                },
                {
                  q: "Was ist, wenn ich noch gar keinen Pflegedienst habe?",
                  a: "Kein Problem! Gerade dann rufen Sie uns an. Wir können auch ohne bestehenden Vertrag sofort helfen. Im ersten Telefonat klären wir die Situation, beim Erstbesuch besprechen wir alles Weitere. Viele unserer Klienten sind genau so zu uns gekommen - durch einen Notfall."
                },
                {
                  q: "Wer kommt bei einem Notfall-Einsatz?",
                  a: "Eine examinierte Pflegefachkraft aus unserem Team. Je nach Verfügbarkeit kann das jemand sein, der Sie noch nicht kennt - aber alle sind erfahren im Umgang mit Notfall-Situationen. Beim zweiten Besuch versuchen wir dann, eine feste Bezugsperson für Sie einzuteilen."
                },
                {
                  q: "Was soll ich am Telefon sagen?",
                  a: "Schildern Sie einfach kurz die Situation: Was ist passiert? Wo wohnen Sie? Was wird dringend benötigt? Wir stellen dann die richtigen Fragen und finden gemeinsam eine Lösung. Sie müssen kein Pflege-Experte sein - das sind wir."
                },
                {
                  q: "Kann ich auch nachts anrufen?",
                  a: "JA! Unsere Hotline ist wirklich 24/7 besetzt. Auch um 3 Uhr nachts nimmt ein echter Mensch ab (kein Anrufbeantworter). Pflege-Notfälle machen keinen Termin - wir sind immer da."
                }
              ].map((faq, i) => (
                <details key={i} className="bg-white rounded-xl p-6 border border-[#e2e8f0] group">
                  <summary className="font-semibold text-[#1e293b] cursor-pointer hover:text-[#1A52D8] transition-colors list-none flex items-center justify-between">
                    <span>{faq.q}</span>
                    <svg className="w-5 h-5 transform group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <p className="mt-4 text-[#64748b] leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-br from-[#1A52D8] to-[#23D3D3]">
          <div className="container-custom max-w-2xl mx-auto text-center text-white">
            <Phone className="w-16 h-16 mx-auto mb-6 animate-pulse" />
            <h2 className="text-4xl font-bold mb-4">
              Brauchen Sie jetzt Hilfe?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Zögern Sie nicht. Ein Anruf kann alles ändern.
            </p>
            <a href="tel:+4915146173685">
              <div className="text-5xl font-bold mb-2">0151 46173685</div>
            </a>
            <p className="text-white/80">Wir sind für Sie da - jetzt gerade!</p>
          </div>
        </section>
      </main>
    </>
  );
}
